import { useState, useEffect } from "react";
import { 
  Alert,
  Paper,
  Text,
  Button,
  Group,
  Loader,
  Textarea,
  Divider,
  SegmentedControl,
  Badge,
  Affix,
  Transition,
  Notification,
  NumberInput } from "@mantine/core";
import { useMediaQuery, useWindowScroll } from "@mantine/hooks";
import { api, type FamilyWithGuests } from "../services/api";
import { IconCheck, IconX } from "@tabler/icons-react";

type Family = FamilyWithGuests;

interface FamilyDetailsProps {
  familyId: number;
  onBack: () => void;
}

export function FamilyDetails({ familyId, onBack }: FamilyDetailsProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [scroll] = useWindowScroll();
  const [family, setFamily] = useState<Family | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [guestConfirmations, setGuestConfirmations] = useState<Record<number, boolean | null>>({});
  const [guestAges, setGuestAges] = useState<Record<number, number | null>>({});
  const [guestBrunchConfirmations, setGuestBrunchConfirmations] = useState<Record<number, boolean | null>>({});
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string>("");
  const [contactEmail, setContactEmail] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadFamily();
  }, [familyId]);

  const loadFamily = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const familyData = await api.getFamilyById(familyId);
      setFamily(familyData);
      
      // Initialize confirmations state
      const confirmations: Record<number, boolean | null> = {};
      const ages: Record<number, number | null> = {};
      const brunchConfirmations: Record<number, boolean | null> = {};
      familyData.guests.forEach((guest) => {
        confirmations[guest.id] = guest.confirmed;
        ages[guest.id] = guest.age;
        brunchConfirmations[guest.id] = guest.brunch_confirmed;
      });
      setGuestConfirmations(confirmations);
      setGuestAges(ages);
      setGuestBrunchConfirmations(brunchConfirmations);
      
      // Initialize dietary restrictions
      setDietaryRestrictions(familyData.dietary_restrictions || "");
      
      // Initialize contact email
      setContactEmail(familyData.contact_email || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load family details");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmationChange = (guestId: number, value: string) => {
    let confirmed: boolean | null;
    
    if (value === "yes") {
      confirmed = true;
    } else if (value === "no") {
      confirmed = false;
    } else {
      confirmed = null;
    }
    
    setGuestConfirmations(prev => ({
      ...prev,
      [guestId]: confirmed
    }));
    
    // Only keep brunch confirmation if guest confirms they WILL attend
    // Reset for both "no" and "no response"
    if (confirmed !== true) {
      setGuestBrunchConfirmations(prev => ({
        ...prev,
        [guestId]: null
      }));
    }
    
    setHasChanges(true);
  };

  const handleAgeChange = (guestId: number, value: number | string) => {
    const ageValue = typeof value === 'string' ? null : value;
    setGuestAges(prev => ({
      ...prev,
      [guestId]: ageValue
    }));
    setHasChanges(true);
  };

  const handleBrunchConfirmationChange = (guestId: number, value: string) => {
    let brunchConfirmed: boolean | null;
    
    if (value === "yes") {
      brunchConfirmed = true;
    } else if (value === "no") {
      brunchConfirmed = false;
    } else {
      brunchConfirmed = null;
    }
    
    setGuestBrunchConfirmations(prev => ({
      ...prev,
      [guestId]: brunchConfirmed
    }));
    setHasChanges(true);
  };

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/family/${family?.unique_link}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleSave = async () => {
    if (!family) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Update each guest's confirmation
      const guestUpdates = Object.entries(guestConfirmations)
        .map(([guestId, confirmed]) => 
          api.updateGuestConfirmation(parseInt(guestId), confirmed)
        );

      // Update ages for child guests that are confirmed
      const ageUpdates = Object.entries(guestAges)
        .filter(([guestId]) => {
          const guest = family.guests.find(g => g.id === parseInt(guestId));
          return guest?.is_child && guestConfirmations[parseInt(guestId)] === true;
        })
        .map(([guestId, age]) => 
          api.updateGuestAge(parseInt(guestId), age)
        );

      // Update brunch confirmations for guests that are invited and confirmed attending
      const brunchUpdates = Object.entries(guestBrunchConfirmations)
        .filter(([guestId]) => {
          const guest = family.guests.find(g => g.id === parseInt(guestId));
          return guest?.brunch_invited && guestConfirmations[parseInt(guestId)] === true;
        })
        .map(([guestId, brunchConfirmed]) => 
          api.updateGuestBrunchConfirmation(parseInt(guestId), brunchConfirmed)
        );

      // Update dietary restrictions
      const dietaryUpdate = api.updateFamilyDietaryRestrictions(family.id, dietaryRestrictions);
      
      // Update contact email (always update, even if empty)
      const emailUpdate = api.updateFamilyContactEmail(family.id, contactEmail);

      await Promise.all([...guestUpdates, ...ageUpdates, ...brunchUpdates, dietaryUpdate, emailUpdate]);
      
      setSuccessMessage("C'est noté !");
      setHasChanges(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de sauvegarder les modifications. Veuillez réessayer.");
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Paper p="xl" shadow="sm" style={{ textAlign: "center" }}>
        <Loader size="lg" />
        <Text style={{ marginTop: "1rem" }}>Recherche de votre invitation...</Text>
      </Paper>
    );
  }

  if (error && !family) {
    return (
      <Paper p="xl" shadow="sm">
        <Alert color="red" title="Error" style={{ marginBottom: "1rem" }}>
          {error}
        </Alert>
        <Button onClick={onBack}>Retour à la recherche</Button>
      </Paper>
    );
  }

  if (!family) {
    return null;
  }

  const confirmedCount = Object.values(guestConfirmations).filter(c => c === true).length;
  const declinedCount = Object.values(guestConfirmations).filter(c => c === false).length;
  const noResponseCount = Object.values(guestConfirmations).filter(c => c === null).length;
  const totalGuests = family.guests.length;

  const getConfirmationValue = (confirmed: boolean | null): string => {
    if (confirmed === true) return "yes";
    if (confirmed === false) return "no";
    return "pending";
  };

  const getGuestStatusColor = (confirmed: boolean | null): string => {
    if (confirmed === true) return "#40c057";
    if (confirmed === false) return "#fa5252";
    return "#e9ecef";
  };

  const xIcon = <IconX size={20} />;
  const checkIcon = <IconCheck size={20} />;

  return (
    <div>
      <Affix position={{ top: 20, right: 20 }}>
        {successMessage && (
          <Notification color="teal" title="👌" icon={checkIcon} mt="md">
            {successMessage}
          </Notification>
        )}

        {error && (
          <Notification color="red" title="Erreur" icon={xIcon} mt="md">
            {error}
          </Notification>
        )}
      </Affix>
      <Paper p="xl" shadow="sm" style={{ marginBottom: "2rem" }}>
        <Button variant="subtle" onClick={onBack}>
          ← Retour à la recherche
        </Button>
        <Group justify="space-between" style={{ marginBottom: "1rem" }}>
          <div>
            <Text size="xl" fw={700}>{family.family_name}</Text>
            <Group gap="xs" style={{ marginTop: "0.5rem" }}>
              <Badge color="green" size="lg">{confirmedCount} Présents</Badge>
              <Badge color="red" size="lg">{declinedCount} Absents</Badge>
              {noResponseCount > 0 && (
                <Badge color="gray" size="lg">{noResponseCount} Sans réponse</Badge>
              )}
            </Group>
          </div>
        </Group>
        <div style={{ marginTop: "1.5rem" }}>
          <Text size="lg" fw={600} style={{ marginBottom: "1rem" }}>
            Liste des invités
          </Text>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {family.guests.map((guest) => {
              const confirmation = guestConfirmations[guest.id] ?? null;
              const age = guestAges[guest.id] ?? null;
              const brunchConfirmation = guestBrunchConfirmations[guest.id] ?? null;
              const showAgeInput = guest.is_child && confirmation === true;
              const showBrunchInput = guest.brunch_invited && confirmation === true;
              
              return (
                <Paper
                  key={guest.id}
                  p="md"
                  shadow="xs"
                  style={{ 
                    border: `2px solid ${getGuestStatusColor(confirmation)}`,
                    transition: "all 0.2s"
                  }}
                >
                  <div style={{ marginBottom: "1rem" }}>
                    <Group gap="xs">
                      <Text fw={500} size="lg">
                        {guest.first_name} {guest.last_name}
                      </Text>
                      {guest.is_child && (
                        <Badge color="blue" size="sm" variant="light">
                          👶 Enfant
                        </Badge>
                      )}
                      {guest.brunch_invited && (
                        <Badge color="orange" size="sm" variant="light">
                          🥐 Brunch
                        </Badge>
                      )}
                    </Group>
                  </div>
                  
                  <SegmentedControl
                    value={getConfirmationValue(confirmation)}
                    onChange={(value) => handleConfirmationChange(guest.id, value)}
                    data={[
                      { label: "✓ Présent!", value: "yes" },
                      { label: "✗ Malheureusement non", value: "no" },
                      { label: "Sans réponse", value: "pending" }
                    ]}
                    fullWidth
                    color={confirmation === true ? "green" : confirmation === false ? "red" : "gray"}
                    orientation={isMobile ? "vertical" : "horizontal"}
                  />

                  {showAgeInput && (
                    <div style={{ marginTop: "1rem" }}>
                      <NumberInput
                        label="Âge à la date de la fête"
                        placeholder="Âge en septembre 2026"
                        value={age ?? ''}
                        onChange={(value) => handleAgeChange(guest.id, value)}
                        min={0}
                        max={18}
                        required
                        error={age === null ? "L'âge est requis pour les enfants qui participent" : undefined}
                      />
                    </div>
                  )}

                  {showBrunchInput && (
                    <div style={{ marginTop: "1rem" }}>
                      <Divider my="sm" label="Brunch du lendemain" labelPosition="center" />
                      <Text size="sm" c="dimmed" style={{ marginBottom: "0.5rem" }}>
                        Serez-vous présent au brunch du lendemain ?
                      </Text>
                      <SegmentedControl
                        value={getConfirmationValue(brunchConfirmation)}
                        onChange={(value) => handleBrunchConfirmationChange(guest.id, value)}
                        data={[
                          { label: "✓ Oui", value: "yes" },
                          { label: "✗ Non", value: "no" },
                          { label: "Sans réponse", value: "pending" }
                        ]}
                        fullWidth
                        color={brunchConfirmation === true ? "teal" : brunchConfirmation === false ? "gray" : "gray"}
                        orientation={isMobile ? "vertical" : "horizontal"}
                      />
                    </div>
                  )}
                </Paper>
              );
            })}
          </div>
        </div>

        <Divider my="xl" />

        <div>
          <Text size="lg" fw={600} style={{ marginBottom: "0.5rem" }}>
            Email de contact
          </Text>
          <Text size="sm" c="dimmed" style={{ marginBottom: "1rem" }}>
            Pour que les éventuelles missives arrivent
          </Text>
          
          <Textarea
            placeholder="votre.email@exemple.fr"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.currentTarget.value);
              setHasChanges(true);
            }}
            minRows={1}
            maxRows={1}
          />
        </div>

        <Divider my="xl" />

        <div>
          <Text size="lg" fw={600} style={{ marginBottom: "0.5rem" }}>
            Restrictions alimentaires & Allergies
          </Text>
          <Text size="sm" c="dimmed" style={{ marginBottom: "1rem" }}>
            Pour que tout le monde profite du banquet !
          </Text>
          
          <Textarea
            placeholder="E.g., végétarien, sans gluten, allergie aux noix, etc."
            value={dietaryRestrictions}
            onChange={(e) => {
              setDietaryRestrictions(e.currentTarget.value);
              setHasChanges(true);
            }}
            minRows={3}
            maxRows={6}
            autosize
          />
        </div>
      </Paper>

      <Affix position={{ bottom: 20, right: 20 }}>
        <Transition transition="slide-up" mounted={scroll.y > 100 || hasChanges}>
          {(transitionStyles) => (
            <Button
              size="lg"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              style={{
                ...transitionStyles,
                minWidth: isMobile ? "auto" : "200px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              }}
              leftSection={isSaving ? <Loader size="sm" color="white" /> : null}
            >
              {isSaving ? "Sauvegarde..." : "💾 Sauvegarder"}
            </Button>
          )}
        </Transition>
      </Affix>
    </div>
  );
}
